import { useEffect, useState, useMemo } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Spinner } from '../components/ui/spinner';
import { MapLocationButton } from '../components/MapLocationButton';
import { Bus, Train, Search, MapPin, Navigation } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import axios from 'axios';
import 'leaflet/dist/leaflet.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fix Leaflet marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom icons for bus and tram
const busIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const tramIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Custom cluster icon
const createClusterCustomIcon = (cluster) => {
  const count = cluster.getChildCount();
  let size = 'small';
  let className = 'bg-[#00599c]';
  
  if (count >= 100) {
    size = 'large';
    className = 'bg-[#00599c]';
  } else if (count >= 10) {
    size = 'medium';
    className = 'bg-[#e5eef5]';
  }
  
  const sizeMap = {
    small: 30,
    medium: 40,
    large: 50
  };
  
  return L.divIcon({
    html: `<div class="cluster-icon ${className}" style="
      width: ${sizeMap[size]}px;
      height: ${sizeMap[size]}px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-weight: bold;
      font-size: ${size === 'large' ? '14px' : '12px'};
      box-shadow: 0 4px 12px rgba(0,0,0,0.3);
      background-color: #00599c;
    ">${count}</div>`,
    className: 'custom-cluster-icon',
    iconSize: L.point(sizeMap[size], sizeMap[size], true),
  });
};

export function TransportPage() {
  const { language } = useLanguage();
  const [data, setData] = useState({ bus_stops: [], tram_stops: [] });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  const t = {
    title: language === 'es' ? 'Transporte Público' : 'Public Transport',
    subtitle: language === 'es' ? 'Paradas de guaguas y tranvía en Tenerife' : 'Bus and tram stops in Tenerife',
    bus: language === 'es' ? 'Guagua' : 'Bus',
    tram: language === 'es' ? 'Tranvía' : 'Tram',
    all: language === 'es' ? 'Todos' : 'All',
    searchPlaceholder: language === 'es' ? 'Buscar parada por nombre...' : 'Search stop by name...',
    totalStops: language === 'es' ? 'paradas en total' : 'total stops',
    showing: language === 'es' ? 'Mostrando' : 'Showing',
    of: language === 'es' ? 'de' : 'of',
    openInGoogleMaps: language === 'es' ? 'Abrir en Google Maps' : 'Open in Google Maps',
  };

  useEffect(() => {
    const fetchTransport = async () => {
      try {
        const response = await axios.get(`${API}/transport`);
        setData(response.data);
      } catch (error) {
        console.error('Error fetching transport:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTransport();
  }, []);

  // Combine and process all stops
  const allStops = useMemo(() => {
    const busStops = (data.bus_stops || []).map(s => ({
      ...s,
      type: 'bus',
      name: s.parada_nombre || s.nombre || s.name || 'Parada',
      lat: s.latitud || s.lat,
      lon: s.longitud || s.lon,
    }));
    const tramStops = (data.tram_stops || []).map(s => ({
      ...s,
      type: 'tram',
      name: s.parada_nombre || s.nombre || s.name || 'Parada Tranvía',
      lat: s.latitud || s.lat,
      lon: s.longitud || s.lon,
    }));
    return [...busStops, ...tramStops];
  }, [data]);

  // Filter stops based on type and search
  const filteredStops = useMemo(() => {
    return allStops.filter((stop) => {
      const matchesType = filter === 'all' || stop.type === filter;
      const matchesSearch = search === '' || 
        stop.name.toLowerCase().includes(search.toLowerCase());
      return matchesType && matchesSearch && stop.lat && stop.lon;
    });
  }, [allStops, filter, search]);

  // Tenerife center
  const centerPosition = [28.4136, -16.5589];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Bus className="w-5 h-5 text-[#00599c]" />
          <span className="text-sm font-medium text-[#00599c] uppercase tracking-wider">
            TITSA & Tranvía
          </span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#252a34] font-['Outfit'] mb-2" data-testid="transport-title">
          {t.title}
        </h1>
        <p className="text-lg text-[#252a34]/70">{t.subtitle}</p>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#252a34]/40" />
          <Input
            type="text"
            placeholder={t.searchPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 h-12 bg-white border-slate-200"
            data-testid="transport-search"
          />
        </div>
        <div className="flex gap-2">
          {[
            { key: 'all', label: t.all, icon: null },
            { key: 'bus', label: t.bus, icon: Bus },
            { key: 'tram', label: t.tram, icon: Train },
          ].map((type) => {
            const Icon = type.icon;
            return (
              <button
                key={type.key}
                onClick={() => setFilter(type.key)}
                data-testid={`filter-${type.key}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  filter === type.key
                    ? 'bg-[#00599c] text-white'
                    : 'bg-white text-[#252a34] border border-slate-200 hover:bg-slate-50'
                }`}
              >
                {Icon && <Icon className="w-4 h-4" />}
                {type.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Stats Bar */}
      <div className="flex items-center justify-between mb-4 text-sm text-[#252a34]/60">
        <div className="flex items-center gap-2">
          <MapPin className="w-4 h-4" />
          <span>
            {t.showing} <strong className="text-[#00599c]">{filteredStops.length.toLocaleString()}</strong> {t.of} {data.total_stops?.toLocaleString() || allStops.length.toLocaleString()} {t.totalStops}
          </span>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-[#00599c]"></div>
            <span>{t.bus}</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-[#22c55e]"></div>
            <span>{t.tram}</span>
          </div>
        </div>
      </div>

      {/* Map */}
      <Card className="overflow-hidden border-slate-200" data-testid="transport-map-container">
        <div className="h-[500px] md:h-[600px]">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-full bg-slate-50">
              <Spinner size="lg" />
              <p className="mt-4 text-[#252a34]/60 text-sm font-medium">
                {language === 'es' ? 'Cargando paradas de transporte...' : 'Loading transport stops...'}
              </p>
            </div>
          ) : (
            <MapContainer
              center={centerPosition}
              zoom={10}
              style={{ height: '100%', width: '100%' }}
              className="z-0"
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <MapLocationButton language={language} />
              <MarkerClusterGroup
                chunkedLoading
                iconCreateFunction={createClusterCustomIcon}
                maxClusterRadius={60}
                spiderfyOnMaxZoom={true}
                showCoverageOnHover={false}
              >
                {filteredStops.map((stop, idx) => (
                  <Marker
                    key={`${stop.type}-${stop.parada_id || idx}`}
                    position={[stop.lat, stop.lon]}
                    icon={stop.type === 'bus' ? busIcon : tramIcon}
                  >
                    <Popup>
                      <div className="font-['Outfit'] min-w-[180px]">
                        <div className="flex items-center gap-2 mb-2">
                          {stop.type === 'bus' ? (
                            <Bus className="w-4 h-4 text-[#00599c]" />
                          ) : (
                            <Train className="w-4 h-4 text-[#22c55e]" />
                          )}
                          <span className="text-xs uppercase font-medium text-[#252a34]/60">
                            {stop.type === 'bus' ? t.bus : t.tram}
                          </span>
                        </div>
                        <h3 className="font-bold text-[#252a34] text-base mb-1">
                          {stop.name}
                        </h3>
                        {stop.parada_url && (
                          <a 
                            href={stop.parada_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-xs text-[#00599c] hover:underline"
                          >
                            Ver horarios →
                          </a>
                        )}
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${stop.lat},${stop.lon}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-2 flex items-center justify-center gap-1 w-full px-3 py-1.5 bg-[#1a73e8] text-xs font-medium rounded hover:bg-[#1557b0] transition-colors"
                          style={{ color: 'white' }}
                        >
                          <Navigation className="w-3 h-3" style={{ color: 'white' }} />
                          {t.openInGoogleMaps}
                        </a>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MarkerClusterGroup>
            </MapContainer>
          )}
        </div>
      </Card>
    </div>
  );
}
